import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from "recharts";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Phone,
  Car,
  Clock,
  TrendingUp,
  CheckCircle,
  ArrowUpDown,
  Radio,
  ShieldAlert
} from "lucide-react";
import { format } from "date-fns";
import Tickets from "@/components/Tickets";

// My Dashboard: the unit's stats (Overview) and their Tickets. A ticket editor
// with no stats of their own (no linked unit) still gets the Tickets tab —
// Overview's "No unit linked" stays inside its own tab.
const DASH_TAB_KEY = "memberDashboardTab";

export default function MemberDashboard() {
  const { user } = useAuth();
  // Admin can hide the Tickets tab per user (Settings → Edit User → My
  // Dashboard Tabs); ticket editors always keep it.
  const ticketsTab = user?.role === "admin" || !!user?.tickets_access || !user?.tickets_tab_hidden;
  const [tab, setTabState] = useState(() => {
    try { return localStorage.getItem(DASH_TAB_KEY) === "tickets" ? "tickets" : "overview"; } catch { return "overview"; }
  });
  const setTab = (t) => {
    setTabState(t);
    try { localStorage.setItem(DASH_TAB_KEY, t); } catch { /* ignore */ }
  };
  if (!ticketsTab) return <MemberOverview />;
  const shown = tab;
  return (
    <div className="space-y-4">
      <div className="flex w-full sm:inline-flex sm:w-auto rounded-md overflow-hidden border border-slate-300 dark:border-slate-700">
        {[["overview", "Overview"], ["tickets", "Tickets"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            className={`flex-1 sm:flex-none px-4 h-9 text-sm font-semibold ${shown === k ? "bg-blue-600 text-white" : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
            {l}
          </button>
        ))}
      </div>
      {shown === "tickets" ? <Tickets user={user} /> : <MemberOverview />}
    </div>
  );
}

function MemberOverview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Incidents dialog state
  const [showCallsDialog, setShowCallsDialog] = useState(false);
  const [memberIncidents, setMemberIncidents] = useState([]);
  const [loadingIncidents, setLoadingIncidents] = useState(false);
  const [incidentSort, setIncidentSort] = useState("newest"); // "newest" or "oldest"
  const [incidentDateFrom, setIncidentDateFrom] = useState("");
  const [incidentDateTo, setIncidentDateTo] = useState("");

  // Arrests dialog state (reuses memberIncidents + the same sort/date filters)
  const [showArrestsDialog, setShowArrestsDialog] = useState(false);

  // Dispatched dialog state
  const [showDispatchedDialog, setShowDispatchedDialog] = useState(false);
  const [dispatchedIncidents, setDispatchedIncidents] = useState([]);
  const [loadingDispatched, setLoadingDispatched] = useState(false);
  const [dispatchedSort, setDispatchedSort] = useState("newest");

  const hasIncidentAccess = user?.role === "admin" || (user?.page_access || []).includes("incidents");

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/member-dashboard/stats`, { withCredentials: true });
      setStats(res.data);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch member dashboard stats:", err);
      if (err.response?.status === 400) {
        setError("No unit linked to your account. Please contact an administrator.");
      } else {
        setError("Failed to load dashboard statistics.");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMemberIncidents = async () => {
    setLoadingIncidents(true);
    try {
      const res = await axios.get(`${API}/member-dashboard/incidents`, { withCredentials: true });
      setMemberIncidents(res.data.incidents || []);
    } catch (error) {
      console.error("Failed to fetch member incidents:", error);
    } finally {
      setLoadingIncidents(false);
    }
  };

  const fetchDispatched = async () => {
    setLoadingDispatched(true);
    try {
      const res = await axios.get(`${API}/member-dashboard/dispatched`, { withCredentials: true });
      setDispatchedIncidents(res.data.incidents || []);
    } catch (error) {
      console.error("Failed to fetch dispatched data:", error);
    } finally {
      setLoadingDispatched(false);
    }
  };

  const handleDispatchedClick = () => {
    setDispatchedSort("newest");
    setShowDispatchedDialog(true);
    fetchDispatched();
  };

  const getFilteredDispatched = () => {
    const sorted = [...dispatchedIncidents];
    sorted.sort((a, b) => {
      const dateA = a.date_time || "";
      const dateB = b.date_time || "";
      return dispatchedSort === "newest" ? dateB.localeCompare(dateA) : dateA.localeCompare(dateB);
    });
    return sorted;
  };

  const handleCallsTotalClick = () => {
    setShowCallsDialog(true);
    setIncidentDateFrom("");
    setIncidentDateTo("");
    fetchMemberIncidents();
  };

  const handleArrestsClick = () => {
    setShowArrestsDialog(true);
    setIncidentDateFrom("");
    setIncidentDateTo("");
    fetchMemberIncidents();
  };

  // Arrest-linked incidents = the member-incidents list, filtered to arrests,
  // reusing the same date-range + sort controls as the Calls (Total) dialog.
  const getFilteredArrests = () => getFilteredIncidents().filter(inc => inc.has_arrest);

  const getFilteredIncidents = () => {
    let filtered = [...memberIncidents];

    // Date filters
    if (incidentDateFrom) {
      filtered = filtered.filter(inc => inc.date_time && inc.date_time >= incidentDateFrom);
    }
    if (incidentDateTo) {
      const toEnd = incidentDateTo + "T23:59:59";
      filtered = filtered.filter(inc => inc.date_time && inc.date_time <= toEnd);
    }

    // Sort
    filtered.sort((a, b) => {
      const dateA = a.date_time || "";
      const dateB = b.date_time || "";
      return incidentSort === "newest" ? dateB.localeCompare(dateA) : dateA.localeCompare(dateB);
    });

    return filtered;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return format(new Date(dateStr), "MM/dd/yyyy");
    } catch {
      return dateStr.slice(0, 10);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-slate-400 text-lg">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="member-dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold uppercase tracking-tight">Member Dashboard</h1>
          <p className="text-sm text-slate-400">
            Unit: <span className="text-blue-400 font-semibold">{stats?.unit_name}</span> | Year: {stats?.year}
          </p>
        </div>
      </div>

      {/* Stats Overview - Calls Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <Card className="dashboard-widget">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl sm:text-3xl font-bold text-cyan-400">{stats?.totals?.calls_weekly || 0}</p>
                <p className="text-xs sm:text-sm text-slate-500 uppercase">Calls (7 Days)</p>
              </div>
              <Phone className="w-6 h-6 sm:w-8 sm:h-8 text-cyan-500/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="dashboard-widget">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl sm:text-3xl font-bold text-blue-400">{stats?.totals?.calls || 0}</p>
                <p className="text-xs sm:text-sm text-slate-500 uppercase">Calls YTD</p>
              </div>
              <Phone className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500/30" />
            </div>
          </CardContent>
        </Card>

        <Card
          className="dashboard-widget cursor-pointer hover:bg-slate-800/50 transition-colors"
          onClick={handleCallsTotalClick}
        >
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl sm:text-3xl font-bold text-indigo-400">{stats?.totals?.calls_total || 0}</p>
                <p className="text-xs sm:text-sm text-slate-500 uppercase">Calls (Total)</p>
              </div>
              <Phone className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-500/30" />
            </div>
          </CardContent>
        </Card>

        <Card
          className="dashboard-widget cursor-pointer hover:bg-slate-800/50 transition-colors"
          onClick={handleArrestsClick}
        >
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl sm:text-3xl font-bold text-emerald-400">{stats?.totals?.arrests || 0}</p>
                <p className="text-xs sm:text-sm text-slate-500 uppercase">Arrests</p>
              </div>
              <ShieldAlert className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-500/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stats Overview - Patrol Row */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        <Card className="dashboard-widget">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl sm:text-3xl font-bold text-emerald-400">{stats?.totals?.sessions || 0}</p>
                <p className="text-xs sm:text-sm text-slate-500 uppercase">Patrol Sessions</p>
              </div>
              <Car className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-500/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="dashboard-widget">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl sm:text-3xl font-bold text-amber-400">{stats?.totals?.hours || 0}</p>
                <p className="text-xs sm:text-sm text-slate-500 uppercase">Patrol Hours</p>
              </div>
              <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-amber-500/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dispatcher Stats - only show if user has dispatched calls */}
      {stats?.totals?.dispatched > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:gap-4">
          <Card
            className="dashboard-widget cursor-pointer hover:bg-slate-800/50 transition-colors"
            onClick={handleDispatchedClick}
          >
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl sm:text-3xl font-bold text-purple-400">{stats?.totals?.dispatched || 0}</p>
                  <p className="text-xs sm:text-sm text-slate-500 uppercase">Incidents Dispatched</p>
                </div>
                <Radio className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500/30" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Calls Responded Chart */}
      <Card className="dashboard-widget">
        <CardHeader className="pb-2 px-3 sm:px-6">
          <CardTitle className="text-xs sm:text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-blue-500" />
            Calls Responded by Month ({stats?.year})
          </CardTitle>
        </CardHeader>
        <CardContent className="px-2 sm:px-6">
          <div className="h-64 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats?.calls_responded || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#0f172a',
                    border: '1px solid #1e293b',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}
                />
                <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Calls" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Patrol Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
        {/* Patrol Sessions by Month */}
        <Card className="dashboard-widget">
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs sm:text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
              <Car className="w-4 h-4 text-emerald-500" />
              Patrol Sessions by Month
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 sm:px-6">
            <div className="h-48 sm:h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.patrol_sessions || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#0f172a',
                      border: '1px solid #1e293b',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}
                  />
                  <Bar dataKey="sessions" fill="#10b981" radius={[4, 4, 0, 0]} name="Sessions" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Patrol Hours by Month */}
        <Card className="dashboard-widget">
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs sm:text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-500" />
              Patrol Hours by Month
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 sm:px-6">
            <div className="h-48 sm:h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.patrol_hours || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#0f172a',
                      border: '1px solid #1e293b',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}
                    formatter={(value) => [`${value} hrs`, 'Hours']}
                  />
                  <Bar dataKey="hours" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Hours" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Incidents Dialog */}
      <Dialog open={showCallsDialog} onOpenChange={setShowCallsDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-md max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-indigo-400 flex items-center gap-2">
              <Phone className="w-5 h-5" />
              Incidents ({getFilteredIncidents().length})
            </DialogTitle>
          </DialogHeader>

          {/* Sort & Date Filters */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => setIncidentSort(incidentSort === "newest" ? "oldest" : "newest")}
              >
                <ArrowUpDown className="w-3 h-3 mr-1" />
                {incidentSort === "newest" ? "Newest First" : "Oldest First"}
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={incidentDateFrom}
                onChange={(e) => setIncidentDateFrom(e.target.value)}
                className="input-dark h-8 text-xs flex-1"
                placeholder="From"
              />
              <span className="text-slate-500 text-xs">to</span>
              <Input
                type="date"
                value={incidentDateTo}
                onChange={(e) => setIncidentDateTo(e.target.value)}
                className="input-dark h-8 text-xs flex-1"
              />
              {(incidentDateFrom || incidentDateTo) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs text-slate-400 px-2"
                  onClick={() => { setIncidentDateFrom(""); setIncidentDateTo(""); }}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>

          <ScrollArea className="h-[55vh] pr-4">
            {loadingIncidents ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
              </div>
            ) : getFilteredIncidents().length === 0 ? (
              <p className="text-slate-500 text-center py-8">No incidents found</p>
            ) : (
              <div className="space-y-2">
                {getFilteredIncidents().map((inc) => (
                  <div
                    key={inc.incident_id}
                    className={`p-3 bg-slate-800 rounded border border-slate-700 transition-colors ${
                      hasIncidentAccess
                        ? "cursor-pointer hover:bg-slate-700 hover:border-indigo-600/50"
                        : ""
                    }`}
                    onClick={hasIncidentAccess ? () => {
                      setShowCallsDialog(false);
                      navigate(`/incidents/${inc.incident_id}`);
                    } : undefined}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm text-slate-300 flex-1">{inc.headline || "No headline"}</p>
                      <div className="flex items-center gap-1 shrink-0">
                        {inc.from_live_call && (
                          <Badge className="bg-red-600/20 text-red-400 border-red-600/50 text-[10px] px-1.5 py-0">
                            LIVE CALL
                          </Badge>
                        )}
                        {inc.has_arrest && (
                          <Badge className="bg-emerald-600/20 text-emerald-400 border-emerald-600/50 text-[10px] px-1.5 py-0">
                            ARREST
                          </Badge>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{formatDate(inc.date_time)}</p>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Arrests Dialog */}
      <Dialog open={showArrestsDialog} onOpenChange={setShowArrestsDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-md max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-emerald-400 flex items-center gap-2">
              <ShieldAlert className="w-5 h-5" />
              Arrests ({getFilteredArrests().length})
            </DialogTitle>
          </DialogHeader>

          {/* Sort & Date Filters */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => setIncidentSort(incidentSort === "newest" ? "oldest" : "newest")}
              >
                <ArrowUpDown className="w-3 h-3 mr-1" />
                {incidentSort === "newest" ? "Newest First" : "Oldest First"}
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={incidentDateFrom}
                onChange={(e) => setIncidentDateFrom(e.target.value)}
                className="input-dark h-8 text-xs flex-1"
                placeholder="From"
              />
              <span className="text-slate-500 text-xs">to</span>
              <Input
                type="date"
                value={incidentDateTo}
                onChange={(e) => setIncidentDateTo(e.target.value)}
                className="input-dark h-8 text-xs flex-1"
              />
              {(incidentDateFrom || incidentDateTo) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs text-slate-400 px-2"
                  onClick={() => { setIncidentDateFrom(""); setIncidentDateTo(""); }}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>

          <ScrollArea className="h-[55vh] pr-4">
            {loadingIncidents ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
              </div>
            ) : getFilteredArrests().length === 0 ? (
              <p className="text-slate-500 text-center py-8">No arrests found</p>
            ) : (
              <div className="space-y-2">
                {getFilteredArrests().map((inc) => (
                  <div
                    key={inc.incident_id}
                    className={`p-3 bg-slate-800 rounded border border-slate-700 transition-colors ${
                      hasIncidentAccess
                        ? "cursor-pointer hover:bg-slate-700 hover:border-emerald-600/50"
                        : ""
                    }`}
                    onClick={hasIncidentAccess ? () => {
                      setShowArrestsDialog(false);
                      navigate(`/incidents/${inc.incident_id}`);
                    } : undefined}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm text-slate-300 flex-1">{inc.headline || "No headline"}</p>
                      <div className="flex items-center gap-1 shrink-0">
                        {inc.from_live_call && (
                          <Badge className="bg-red-600/20 text-red-400 border-red-600/50 text-[10px] px-1.5 py-0">
                            LIVE CALL
                          </Badge>
                        )}
                        <Badge className="bg-emerald-600/20 text-emerald-400 border-emerald-600/50 text-[10px] px-1.5 py-0">
                          ARREST
                        </Badge>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{formatDate(inc.date_time)}</p>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Dispatched Dialog */}
      <Dialog open={showDispatchedDialog} onOpenChange={setShowDispatchedDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-md max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-purple-400 flex items-center gap-2">
              <Radio className="w-5 h-5" />
              Incidents Dispatched ({getFilteredDispatched().length})
            </DialogTitle>
          </DialogHeader>

          <div className="flex items-center gap-2">
            {/* Counter: how many of the dispatched incidents were arrests */}
            <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-emerald-600/15 border border-emerald-600/40">
              <ShieldAlert className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-xs font-semibold text-emerald-300">
                {getFilteredDispatched().filter(inc => inc.has_arrest).length} {getFilteredDispatched().filter(inc => inc.has_arrest).length === 1 ? "Arrest" : "Arrests"}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs ml-auto"
              onClick={() => setDispatchedSort(dispatchedSort === "newest" ? "oldest" : "newest")}
            >
              <ArrowUpDown className="w-3 h-3 mr-1" />
              {dispatchedSort === "newest" ? "Newest" : "Oldest"}
            </Button>
          </div>

          <ScrollArea className="h-[55vh] pr-4">
            {loadingDispatched ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
              </div>
            ) : getFilteredDispatched().length === 0 ? (
              <p className="text-slate-500 text-center py-8">No incidents found</p>
            ) : (
              <div className="space-y-2">
                {getFilteredDispatched().map((inc) => (
                  <div
                    key={inc.incident_id}
                    className={`p-3 bg-slate-800 rounded border border-slate-700 transition-colors ${
                      hasIncidentAccess
                        ? "cursor-pointer hover:bg-slate-700 hover:border-purple-600/50"
                        : ""
                    }`}
                    onClick={hasIncidentAccess ? () => {
                      setShowDispatchedDialog(false);
                      navigate(`/incidents/${inc.incident_id}`);
                    } : undefined}
                  >
                    {/* Headline gets the full width so it wraps cleanly; the
                        badges wrap onto their own row below instead of squeezing
                        the headline into a tall narrow column. */}
                    <p className="text-sm text-slate-300">{inc.headline || "No headline"}</p>
                    {(inc.from_live_call || inc.has_arrest || inc.incident_type) && (
                      <div className="flex flex-wrap items-center gap-1 mt-1.5">
                        {inc.from_live_call && (
                          <Badge className="bg-red-600/20 text-red-400 border-red-600/50 text-[10px] px-1.5 py-0">
                            LIVE CALL
                          </Badge>
                        )}
                        {inc.has_arrest && (
                          <Badge className="bg-emerald-600/20 text-emerald-400 border-emerald-600/50 text-[10px] px-1.5 py-0">
                            ARREST
                          </Badge>
                        )}
                        {inc.incident_type && (
                          <Badge className="bg-purple-600/20 text-purple-400 border-purple-600/50 text-[10px] px-1.5 py-0">
                            {inc.incident_type}
                          </Badge>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-slate-500 mt-1">#{inc.incident_id} | {formatDate(inc.date_time)}</p>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
